<template>
  <div>
    <!-- Available vehicles section -->
    <div v-if="availableVehicles && availableVehicles.length > 0">
      <!-- Organization vehicles table -->
      <div v-if="organizationVehicles && organizationVehicles.length > 0">
        <p class="flex items-center mb-2">
          Pasirinkite savo organizacijos transporto priemonę:
        </p>
        <table class="table">
          <thead>
            <tr>
              <th class="w-16"></th>
              <th>Modelis</th>
              <th>Transporto priemonė</th>
              <th>Organizacija</th>
              <th>Vietų skaičius</th>
              <th>Užimtumas</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="vehicle in organizationVehicles"
              :key="vehicle.id"
              :class="[
                selectedVehicleId === vehicle.id
                  ? 'bg-primary-content'
                  : 'bg-base-100',
              ]"
            >
              <td class="w-16">
                <DefaultIcon
                  @click="selectVehicle(vehicle)"
                  name="plus"
                  size="w-6 h-6"
                  class="btn btn-circle btn-sm btn-ghost text-primary"
                />
              </td>
              <td>
                <div class="avatar">
                  <div class="w-24 rounded-sm">
                    <img :src="vehicle.img_url" :alt="vehicle.model" />
                  </div>
                </div>
              </td>
              <td>
                {{ vehicle.reg_nr }} <br />
                <span class="text-xs text-base-content/60">{{ vehicle.model }}</span>
              </td>
              <td>
                {{ vehicle.organization_name }} <br />
                <span class="text-xs text-base-content/60">{{ vehicle.address }}</span>
              </td>
              <td>
                Įprastos: {{ vehicle.seats_regular }} <br />
                Neįgaliems: {{ vehicle.seats_disabled }}
              </td>
              <td>
                <ol v-if="vehicle.occupied_times.length > 0">
                  <li
                    v-for="(occupied_time, index) in vehicle.occupied_times"
                    :key="index"
                  >
                    {{ occupied_time.time_start }}-{{
                      occupied_time.time_finish
                    }}
                  </li>
                </ol>

                <div v-else>Laisva visą dieną</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="py-4 text-base-content/60">
        Nurodytai datai ir keleivių skaičiui tinkamos Jūsų organizacijos
        transporto priemonės nerasta. Bandykite pasirinkti kitos organizacijos
        transporto priemonę.
      </div>

      <!-- Other organizations vehicles table (collapsible) -->
      <div
        v-if="otherOrganizationVehicles && otherOrganizationVehicles.length > 0"
        class="bg-base-100 border-base-300 collapse collapse-plus border mt-3"
      >
        <input type="checkbox" class="peer" />
        <div class="collapse-title">
          Pasirinkite kitų organizacijų transporto priemonę
        </div>
        <div class="collapse-content">
          <table class="table">
            <thead>
              <tr>
                <th class="w-16"></th>
                <th>Modelis</th>
                <th>Transporto priemonė</th>
                <th>Organizacija</th>
                <th>Vietų skaičius</th>
                <th>Užimtumas</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="vehicle in otherOrganizationVehicles"
                :key="vehicle.id"
                :class="[
                  selectedVehicleId === vehicle.id
                    ? 'bg-primary-content'
                    : 'bg-base-100',
                ]"
              >
                <td class="w-16">
                  <DefaultIcon
                    @click="selectVehicle(vehicle)"
                    name="plus"
                    size="w-6 h-6"
                    class="btn btn-circle btn-sm btn-ghost text-primary"
                  />
                </td>
                <td>
                  <div class="avatar">
                    <div class="w-24 rounded-sm">
                      <img :src="vehicle.img_url" :alt="vehicle.model" />
                    </div>
                  </div>
                </td>
                <td>
                  {{ vehicle.reg_nr }} <br />
                  <span class="text-xs text-base-content/60">{{ vehicle.model }}</span>
                </td>
                <td>
                  {{ vehicle.organization_name }} <br />
                  <span class="text-xs text-base-content/60">{{
                    vehicle.address
                  }}</span>
                </td>
                <td>
                  Įprastos: {{ vehicle.seats_regular }} <br />
                  Neįgaliems: {{ vehicle.seats_disabled }}
                </td>
                <td>
                  <div v-if="vehicle.occupied_times.length > 0">
                    vehicle.occupied_times
                  </div>
                  <div v-else>Laisva visą dieną</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- No vehicles available message -->
    <div v-else class="py-4 text-base-content/60">
      Nurodytai datai ir keleivių skaičiui tinkamos transporto priemonės
      nerasta. Bandykite pavėžėjimą įtraukti į jau suplanuotą pasirinktos dienos
      kelionę.
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";
import { useAuthStore } from "@/stores/auth"; // Adjust path as needed
import DefaultIcon from "@/components/icons/DefaultIcon.vue"; // Adjust path as needed

const props = defineProps({
  availableVehicles: {
    type: Array,
    default: () => [],
  },
  selectedVehicleId: {
    type: [Number, String, null],
    default: null,
  },
});

const emit = defineEmits(["select-vehicle"]);

const authStore = useAuthStore();

// Get the current user's organization name
const userOrganizationName = computed(() => {
  return authStore.user?.organization?.name || "";
});

// Computed property to get vehicles from user's organization
const organizationVehicles = computed(() => {
  if (!props.availableVehicles) return [];

  return props.availableVehicles.filter(
    (vehicle) => vehicle.organization_name === userOrganizationName.value,
  );
});

// Computed property to get vehicles from other organizations
const otherOrganizationVehicles = computed(() => {
  if (!props.availableVehicles) return [];

  return props.availableVehicles.filter(
    (vehicle) => vehicle.organization_name !== userOrganizationName.value,
  );
});

// Function to select a vehicle and emit the selection
const selectVehicle = (vehicle) => {
  emit("select-vehicle", vehicle);
};
</script>
